import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { PayrollAccessService } from '../payroll-access/payroll-access.service';
import { PayrollSnacksAssignmentService } from '../payroll-snacks-assignment/payroll-snacks-assignment.service';
import { SessionService } from '../session/session.service';
import { TeamCompaniesService } from '../teamcompanies/teamcompanies.service';
import { UsersService } from '../users/users.service';

export interface EligibleEmployerCompany {
  id: string;
  name: string;
  type: 'employer';
}

export type SingleCompanyWorkspaceStatus =
  | 'notInitialized'
  | 'ineligible'
  | 'loading'
  | 'active'
  | 'noEligibleCompany'
  | 'multipleCompaniesUnsupported'
  | 'loadFailed';

export type EmployerCompanyWorkspaceContext =
  | 'none'
  | 'nativeCompanyManager'
  | 'superadminCompany';

export interface ActiveEmployerCompanyState {
  initialized: boolean;
  loading: boolean;
  workspaceContext: EmployerCompanyWorkspaceContext;
  singleCompanyWorkspaceStatus: SingleCompanyWorkspaceStatus;
  eligibleCompanies: EligibleEmployerCompany[];
  activeCompany?: any;
  benefitProductIds: string[];
  benefitsReadyProductIds: string[];
  hasBenefits: boolean;
  hasSnacks: boolean;
  error: string;
}

@Injectable({
  providedIn: 'root',
})
export class ActiveEmployerCompanyService {
  private readonly stateSubject = new BehaviorSubject<ActiveEmployerCompanyState>({
    initialized: false,
    loading: false,
    workspaceContext: 'none',
    singleCompanyWorkspaceStatus: 'notInitialized',
    eligibleCompanies: [],
    activeCompany: undefined,
    benefitProductIds: [],
    benefitsReadyProductIds: [],
    hasBenefits: false,
    hasSnacks: false,
    error: '',
  });
  private initializePromise?: Promise<ActiveEmployerCompanyState>;
  private initializedUserId = '';
  private loadGeneration = 0;

  constructor(
    private payrollAccessService: PayrollAccessService,
    private payrollSnacksAssignmentService: PayrollSnacksAssignmentService,
    private sessionService: SessionService,
    private teamCompaniesService: TeamCompaniesService,
    private usersService: UsersService,
  ) {}

  get state(): ActiveEmployerCompanyState {
    return this.stateSubject.value;
  }

  get activeCompany(): any | undefined {
    return this.state.activeCompany;
  }

  stateChanges(): BehaviorSubject<ActiveEmployerCompanyState> {
    return this.stateSubject;
  }

  isSupportedSingleCompanyWorkspaceActive(
    state: ActiveEmployerCompanyState = this.state,
  ): boolean {
    return state.singleCompanyWorkspaceStatus === 'active'
      && !!state.activeCompany;
  }

  isScopedSuperadminCompanyWorkspaceActive(
    state: ActiveEmployerCompanyState = this.state,
  ): boolean {
    return state.workspaceContext === 'superadminCompany'
      && this.isSupportedSingleCompanyWorkspaceActive(state);
  }

  async initialize(forceRefresh = false): Promise<ActiveEmployerCompanyState> {
    const userId = this.sessionService.getUserId();
    if (this.initializedUserId && this.initializedUserId !== userId) {
      this.clear();
    }
    if (!forceRefresh && this.state.initialized && this.initializedUserId === userId) {
      return this.state;
    }
    if (!forceRefresh && this.initializePromise) {
      return this.waitForCurrentInitialization(this.initializePromise, userId);
    }

    const promise = this.initializeForCurrentUser(forceRefresh, ++this.loadGeneration, userId);
    this.initializePromise = promise;
    try {
      return await this.waitForCurrentInitialization(promise, userId);
    } finally {
      if (this.initializePromise === promise) { this.initializePromise = undefined; }
    }
  }

  async selectCompany(companyId: string): Promise<ActiveEmployerCompanyState> {
    if (!this.state.initialized) {
      await this.initialize();
    }

    const normalizedCompanyId = `${companyId || ''}`.trim();
    if (!this.isSupportedSingleCompanyWorkspaceActive()) {
      return this.state;
    }
    if (`${this.state.activeCompany?.id || ''}`.trim() !== normalizedCompanyId) {
      return this.failClosed('The selected employer company is no longer available.');
    }

    return this.state;
  }

  async initializeScopedSuperadminCompany(
    companyId: string,
    forceRefresh = false,
  ): Promise<ActiveEmployerCompanyState> {
    const userId = this.sessionService.getUserId();
    if (this.initializedUserId && this.initializedUserId !== userId) {
      this.clear();
    }

    const normalizedCompanyId = `${companyId || ''}`.trim();
    const generation = ++this.loadGeneration;
    this.initializePromise = undefined;
    const access = await this.payrollAccessService.refresh(forceRefresh);
    if (!this.isCurrentLoad(generation, userId)) { return this.state; }
    if (
      !userId
      || !normalizedCompanyId
      || !this.sessionService.isExactSuperAdmin()
      || access.workspaceContext !== 'fullPayrollAdmin'
      || !access.canShowPayrollAdminNav
    ) {
      return this.replaceState({
        ...this.getEmptyState(),
        initialized: true,
        singleCompanyWorkspaceStatus: 'ineligible',
      });
    }

    if (
      !forceRefresh
      && this.initializedUserId === userId
      && this.isScopedSuperadminCompanyWorkspaceActive()
      && `${this.state.activeCompany?.id || ''}`.trim() === normalizedCompanyId
    ) {
      return this.state;
    }

    this.initializedUserId = userId;
    const selectedCompany: EligibleEmployerCompany = {
      id: normalizedCompanyId,
      name: normalizedCompanyId,
      type: 'employer',
    };
    return this.loadActiveCompany(
      selectedCompany,
      [selectedCompany],
      'superadminCompany',
      generation,
      userId,
    );
  }

  clearScopedSuperadminCompany(): void {
    if (this.state.workspaceContext === 'superadminCompany') {
      this.clear();
    }
  }

  clear(): void {
    this.loadGeneration++;
    this.initializePromise = undefined;
    this.initializedUserId = '';
    this.stateSubject.next({
      initialized: false,
      loading: false,
      workspaceContext: 'none',
      singleCompanyWorkspaceStatus: 'notInitialized',
      eligibleCompanies: [],
      activeCompany: undefined,
      benefitProductIds: [],
      benefitsReadyProductIds: [],
      hasBenefits: false,
      hasSnacks: false,
      error: '',
    });
  }

  private async initializeForCurrentUser(
    forceRefresh: boolean, generation: number, userId: string,
  ): Promise<ActiveEmployerCompanyState> {
    const access = await this.payrollAccessService.refresh(forceRefresh);
    if (!this.isCurrentLoad(generation, userId)) { return this.state; }
    this.initializedUserId = userId;

    if (!userId || access.workspaceContext !== 'nativeCompanyManager') {
      return this.replaceState({
        initialized: true,
        loading: false,
        workspaceContext: 'none',
        singleCompanyWorkspaceStatus: 'ineligible',
        eligibleCompanies: [],
        activeCompany: undefined,
        benefitProductIds: [],
        benefitsReadyProductIds: [],
        hasBenefits: false,
        hasSnacks: false,
        error: '',
      });
    }

    const eligibleCompanies = this.buildEligibleCompanies(access.nativeCompanyManagerRoles);
    if (!eligibleCompanies.length) {
      return this.failClosed(
        'No authorized employer companies are available.',
        eligibleCompanies,
        'noEligibleCompany',
      );
    }
    if (eligibleCompanies.length > 1) {
      return this.failClosed(
        'Multiple authorized employer companies are not supported yet.',
        eligibleCompanies,
        'multipleCompaniesUnsupported',
      );
    }

    const [onlyEligibleCompany] = eligibleCompanies;
    return this.loadActiveCompany(
      onlyEligibleCompany,
      eligibleCompanies,
      'nativeCompanyManager',
      generation,
      userId,
    );
  }

  private async loadActiveCompany(
    selectedCompany: EligibleEmployerCompany,
    eligibleCompanies: EligibleEmployerCompany[],
    workspaceContext: EmployerCompanyWorkspaceContext,
    generation: number,
    userId: string,
  ): Promise<ActiveEmployerCompanyState> {
    this.replaceState({
      ...this.state,
      initialized: false,
      loading: true,
      workspaceContext,
      singleCompanyWorkspaceStatus: 'loading',
      eligibleCompanies,
      activeCompany: undefined,
      benefitProductIds: [],
      benefitsReadyProductIds: [],
      hasBenefits: false,
      hasSnacks: false,
      error: '',
    });

    try {
      const workspaceResponse = await this.teamCompaniesService
        .getPayrollCompanyWorkspaceContext(selectedCompany.id);
      if (!this.isCurrentLoad(generation, userId)) { return this.state; }
      const company = workspaceResponse?.company;
      if (
        !company
        || `${company.id || ''}`.trim() !== selectedCompany.id
        || `${company.type || ''}`.trim().toLowerCase() !== 'employer'
      ) {
        return this.failClosed('The selected employer company could not be authorized.', eligibleCompanies);
      }

      const [onboardingRows, snackLoad] = await Promise.all([
        this.teamCompaniesService.getGroupProductOnboardingStatuses(company.id).catch(() => []),
        this.payrollSnacksAssignmentService.loadSnackRewardsForCompany(
          company.id,
          undefined,
          this.getCurrentBrand(),
          company,
        ).catch(() => ({ snackRewards: [] } as any)),
      ]);
      if (!this.isCurrentLoad(generation, userId)) { return this.state; }
      const benefitProductIds = this.getBenefitProductIds(company);
      const benefitsReadyProductIds = this.getBenefitsReadyProductIds(company, onboardingRows);

      return this.replaceState({
        initialized: true,
        loading: false,
        workspaceContext,
        singleCompanyWorkspaceStatus: 'active',
        eligibleCompanies,
        activeCompany: company,
        benefitProductIds,
        benefitsReadyProductIds,
        hasBenefits: benefitsReadyProductIds.length > 0,
        hasSnacks: (snackLoad?.snackRewards || []).length > 0,
        error: '',
      });
    } catch (error) {
      if (!this.isCurrentLoad(generation, userId)) { return this.state; }
      return this.failClosed(
        'Unable to load the selected employer company.',
        eligibleCompanies,
        'loadFailed',
      );
    }
  }

  private getBenefitProductIds(company: any): string[] {
    const payrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
    const productIds = this.normalizeStringArray(payrollConfig?.products);
    const benefitMap = payrollConfig?.productPayrollBenefitCategoriesMap || {};

    return productIds.filter((productId) => !!`${benefitMap?.[productId]?.category || ''}`.trim());
  }

  private isCurrentLoad(generation: number, userId: string): boolean {
    return generation === this.loadGeneration && userId === this.sessionService.getUserId();
  }

  private async waitForCurrentInitialization(
    pending: Promise<ActiveEmployerCompanyState>, userId: string,
  ): Promise<ActiveEmployerCompanyState> {
    // A shell refresh may supersede a route guard's load during startup. Join
    // that actual pending request rather than returning its transient loading state.
    while (true) {
      const state = await pending;
      const next = this.initializePromise;
      if (!next || next === pending || userId !== this.sessionService.getUserId()) {
        return state;
      }
      pending = next;
    }
  }

  private getBenefitsReadyProductIds(company: any, onboardingRows: any[]): string[] {
    const productIds = this.getBenefitProductIds(company);
    const payrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
    const benefitMap = payrollConfig?.productPayrollBenefitCategoriesMap || {};
    const onboardingStatusByProductId = new Map(
      (Array.isArray(onboardingRows) ? onboardingRows : [])
        .map((row: any) => [`${row?.requestedVendorProductId || ''}`.trim(), row?.status]),
    );

    return productIds.filter((productId) => {
      const mapping = benefitMap?.[productId];
      return mapping?.requiresApproval !== true
        || onboardingStatusByProductId.get(productId) === 'ready';
    });
  }

  private buildEligibleCompanies(roles: any[]): EligibleEmployerCompany[] {
    const companiesById = new Map<string, EligibleEmployerCompany>();
    (Array.isArray(roles) ? roles : []).forEach((role) => {
      const companyId = `${role?.companyId || ''}`.trim();
      if (
        !companyId
        || `${role?.role || ''}`.trim().toLowerCase() !== 'manager'
        || `${role?.type || ''}`.trim().toLowerCase() !== 'employer'
      ) {
        return;
      }
      companiesById.set(companyId, {
        id: companyId,
        name: `${role?.name || role?.companyName || companyId}`.trim(),
        type: 'employer',
      });
    });
    return [...companiesById.values()].sort((left, right) => left.name.localeCompare(right.name));
  }

  private getCurrentBrand(): string {
    const me = this.usersService.me();
    return `${me?.brand?.shortName || me?.brand?.name || me?.brand?.partnerId || ''}`
      .trim()
      .toLowerCase();
  }

  private normalizeStringArray(values: any): string[] {
    return [...new Set((Array.isArray(values) ? values : [])
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean))];
  }

  private failClosed(
    error: string,
    eligibleCompanies: EligibleEmployerCompany[] = [],
    singleCompanyWorkspaceStatus: SingleCompanyWorkspaceStatus = 'loadFailed',
  ): ActiveEmployerCompanyState {
    return this.replaceState({
      initialized: true,
      loading: false,
      workspaceContext: this.state.workspaceContext,
      singleCompanyWorkspaceStatus,
      eligibleCompanies,
      activeCompany: undefined,
      benefitProductIds: [],
      benefitsReadyProductIds: [],
      hasBenefits: false,
      hasSnacks: false,
      error,
    });
  }

  private replaceState(state: ActiveEmployerCompanyState): ActiveEmployerCompanyState {
    this.stateSubject.next(state);
    return state;
  }

  private getEmptyState(): ActiveEmployerCompanyState {
    return {
      initialized: false,
      loading: false,
      workspaceContext: 'none',
      singleCompanyWorkspaceStatus: 'notInitialized',
      eligibleCompanies: [],
      activeCompany: undefined,
      benefitProductIds: [],
      benefitsReadyProductIds: [],
      hasBenefits: false,
      hasSnacks: false,
      error: '',
    };
  }
}
